import { isCustomParam, isServerSideMethod } from './components/TrackingLinkOptionsTab/utils';
import {
    ATTRIBUTION_TAB_IDS,
    CLICK_IMPRESSION_REPORTING_METHODS,
    MACRO_INPUT_TYPES,
    PLATFORMS,
    generalPlatformsIncludeTrackingLinkExtraRulePlatform,
    POSTBACK_EVENT_TYPE_OPTIONS,
    REQUIRED_SERVER_SIDE_MACRO_KEYS,
    TRACKING_LINK_EXTRA_REQUIRED_KEYS,
    TRACKING_LINK_EXTRA_RULE_PLATFORMS,
    STANDARD_TRACKING_LINK_PARAMETERS,
    TRANSLATE_PREFIX,
    URL_QUERY_KEY_REGEX,
    URL_QUERY_VALUE_REGEX,
    WHITESPACE_REGEX,
} from './consts';
import {
    PLATFORM_TEMPLATE_POSTBACK_URL_PATTERNS,
    PLATFORM_TEMPLATE_REQUIRED_QUERY_PARAM_KEYS,
    PLATFORM_TEMPLATE_SECURITY_TOKEN_KEYS,
    POSTBACK_URL_DOMAIN_REGEX,
    POSTBACK_URL_SECURITY_TOKEN_REGEX,
    extractPostbackUrlDomain,
    extractRequiredQueryParamValue,
    extractSecurityToken,
} from './platformTemplateDefaults';
import { resolveTemplateEntries } from './templateEntries';
import { getPostbackEventTypeLabelKey } from './utils';

export const PRIMARY_TRACKING_LINK_KEYS = new Set(
    STANDARD_TRACKING_LINK_PARAMETERS.filter(p => p.primary).map(p => p.singularKey)
);

// Formik error key suffixes that block saving as a draft (structural / format issues that the BE
// schema rejects even in draft mode). Other Formik errors are submit-only required-field misses.
const DRAFT_BLOCKING_ERROR_KEY_SUFFIXES = [
    '.platform_template',
    '.tracking_link_parameters_macro_invalid_chars',
    '.tracking_link_parameters_duplicate_key',
    '.singular_key_invalid_chars',
    '.event_names_duplicate',
    '.event_parameter_key_invalid_chars',
    '.event_names_no_spaces',
    '.parameter_key_invalid_chars',
    '.partner_parameter_key_invalid_chars',
    '.partner_parameter_key_duplicate',
    '.postback_url_domain_invalid',
    '.postback_urls_invalid_domain',
    '.postback_url_security_token_invalid',
    '.postback_url_required_query_param_invalid',
];

export const isDraftBlockingErrorKey = key => DRAFT_BLOCKING_ERROR_KEY_SUFFIXES.some(suffix => key.endsWith(suffix));

const err = key => `${TRANSLATE_PREFIX}.ERRORS.${key}`;

// Default empty check: null/undefined, empty string, or empty array.
// Does NOT treat `false` as empty (handles boolean fields like view_through_campaigns).
const defaultIsEmpty = value =>
    value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);

// Generic runner: for each rule, skip if `when` is false, then check emptiness.
// rule shape: { field, errorKey, when?, isEmpty? }
const validateRequired = (tabId, data, rules) =>
    rules.reduce((errors, { field, errorKey, when, isEmpty = defaultIsEmpty }) => {
        if (when && !when(data)) return errors;
        if (isEmpty(data[field], data)) {
            errors[`formData.${tabId}.${field}`] = err(errorKey);
        }
        return errors;
    }, {});

const GENERAL_RULES = [
    { field: 'platform_template', errorKey: 'PLATFORM_TEMPLATE_REQUIRED' },
    { field: 'platforms', errorKey: 'PLATFORMS_REQUIRED', when: d => !!d.platform_template },
    // Special: platforms_other only required when 'other' is selected
    {
        field: 'platforms_other',
        errorKey: 'PLATFORMS_OTHER_REQUIRED',
        when: d => !!d.platform_template && d.platforms?.includes(PLATFORMS.OTHER),
        isEmpty: v => !v?.trim(),
    },
    { field: 'campaign_objectives', errorKey: 'OBJECTIVES_REQUIRED', when: d => !!d.platform_template },
    // Data scope fields only required when receive_postbacks is explicitly true.
    // Mirrors backend `field_equals("receive_postbacks", True)` — absent ≠ true.
    {
        field: 'installs_data_scope',
        errorKey: 'INSTALLS_DATA_SCOPE_REQUIRED',
        when: d => !!d.platform_template && d.receive_postbacks === true,
    },
    {
        field: 'events_data_scope',
        errorKey: 'EVENTS_DATA_SCOPE_REQUIRED',
        when: d => !!d.platform_template && d.receive_postbacks === true,
    },
];

const TRACKING_LINK_OPTIONS_RULES = [
    { field: 'view_through_campaigns', errorKey: 'VIEW_THROUGH_REQUIRED' },
    { field: 'click_impression_reporting_method', errorKey: 'REPORTING_METHOD_REQUIRED' },
    // Special: elaboration only required when method is 'both'
    {
        field: 'click_impression_reporting_method_elaboration',
        errorKey: 'ELABORATION_REQUIRED',
        when: d => d.click_impression_reporting_method === CLICK_IMPRESSION_REPORTING_METHODS.BOTH,
        isEmpty: v => !v?.trim(),
    },
    // tracking_link_parameters must be non-empty
    {
        field: 'tracking_link_parameters',
        errorKey: 'TRACKING_LINK_PARAMS_REQUIRED',
    },
];

const validateTrackingLinkItems = (params, errors, reportingMethod, generalPlatforms) => {
    const tabId = ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS;
    const hasIncompleteCustomRow = params.some(
        p => isCustomParam(p) && (!p.singular_key || !p.field_name || !p.partner_macro)
    );
    if (hasIncompleteCustomRow) {
        errors[`formData.${tabId}.tracking_link_parameters_item_fields`] = err('TRACKING_LINK_PARAM_FIELDS_REQUIRED');
    }

    // Mandatory rows (server-side required + general-platform extra-rule keys) cannot be deleted
    // by the user, so the per-row "fill the macro or delete the row" hint would be misleading.
    // They already get dedicated errors via TRACKING_LINK_PARAMS_SERVER_SIDE_REQUIRED and the
    // per-cell TRACKING_LINK_PARAM_MACRO_REQUIRED / EXTRA_RULE messages.
    const mandatoryKeys = new Set([
        ...(isServerSideMethod(reportingMethod) ? REQUIRED_SERVER_SIDE_MACRO_KEYS : []),
        ...(generalPlatformsIncludeTrackingLinkExtraRulePlatform(generalPlatforms)
            ? TRACKING_LINK_EXTRA_REQUIRED_KEYS
            : []),
    ]);

    // Custom rows with missing fields already surface via TRACKING_LINK_PARAM_FIELDS_REQUIRED
    // above (covers empty singular_key, field_name, or macro). Skip them here so we don't list
    // empty entries in the macro-required summary.
    const missingMacroFieldNames = params
        .filter(p => !isCustomParam(p) && !mandatoryKeys.has(p.singular_key) && !p.partner_macro?.trim())
        .map(p => p.field_name?.trim() || p.singular_key || '');
    if (missingMacroFieldNames.length) {
        errors[`formData.${tabId}.tracking_link_parameters_macro_required`] = {
            messageKey: err('TRACKING_LINK_PARAM_MACRO_REQUIRED_SUMMARY'),
            fieldNames: missingMacroFieldNames,
        };
    }

    // Split partner_macro invalid-chars offenders into named (has field_name/singular_key) and
    // unnamed (custom rows without either). Named rows get listed by name; unnamed rows collapse
    // to one generic message — emitted only when there are no named offenders to mention.
    // Mirrors the singular_key dedupe pattern (validator-level, no render-side dedupe).
    const macroInvalidParams = params.filter(p => p.partner_macro && !URL_QUERY_VALUE_REGEX.test(p.partner_macro));
    const labelFor = p => p.field_name?.trim() || p.singular_key || '';
    const namedMacroInvalid = macroInvalidParams.map(labelFor).filter(Boolean);
    const hasUnnamedMacroInvalid = macroInvalidParams.some(p => !labelFor(p));
    if (namedMacroInvalid.length) {
        errors[`formData.${tabId}.tracking_link_parameters_macro_invalid_chars`] = {
            messageKey: err('TRACKING_LINK_PARAM_MACRO_INVALID_CHARS_SUMMARY'),
            fieldNames: namedMacroInvalid,
        };
    } else if (hasUnnamedMacroInvalid) {
        errors[`formData.${tabId}.tracking_link_parameters_macro_invalid_chars`] = err(
            'TRACKING_LINK_PARAM_MACRO_INVALID_CHARS_GENERIC_SUMMARY'
        );
    }

    // Emit one Formik error on the first offending row. The error key still ends with
    // `.singular_key_invalid_chars` so it draft-blocks; per-row red borders are computed
    // independently in TrackingLinkParametersTable from row.singularKeyInvalidChars.
    const firstInvalidIndex = params.findIndex(
        p => isCustomParam(p) && p.singular_key && !URL_QUERY_KEY_REGEX.test(p.singular_key)
    );
    if (firstInvalidIndex >= 0) {
        errors[`formData.${tabId}.tracking_link_parameters.${firstInvalidIndex}.singular_key_invalid_chars`] = {
            messageKey: err('TRACKING_LINK_PARAM_SINGULAR_KEY_INVALID_CHARS_SUMMARY'),
        };
    }

    const seen = new Set();
    const hasDuplicateKey = params.some(p => {
        if (!p.singular_key) return false;
        if (seen.has(p.singular_key)) return true;
        seen.add(p.singular_key);
        return false;
    });
    if (hasDuplicateKey) {
        errors[`formData.${tabId}.tracking_link_parameters_duplicate_key`] = err('TRACKING_LINK_PARAM_DUPLICATE_KEY');
    }
};

const validateTrackingLinkOptions = (data, formData = {}) => {
    const errors = validateRequired(ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS, data, TRACKING_LINK_OPTIONS_RULES);
    const params = data.tracking_link_parameters || [];

    const reportingMethod = data.click_impression_reporting_method;
    const generalPlatforms = formData[ATTRIBUTION_TAB_IDS.GENERAL]?.platforms;

    if (params.length) {
        validateTrackingLinkItems(params, errors, reportingMethod, generalPlatforms);
    }

    // Server-side and extra-rule checks are independent — BE emits both when applicable.
    if (isServerSideMethod(reportingMethod)) {
        const missingKeys = REQUIRED_SERVER_SIDE_MACRO_KEYS.filter(key => {
            const param = params.find(p => p.singular_key === key);
            return !param?.partner_macro?.trim();
        });
        if (missingKeys.length) {
            errors[`formData.${ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS}.tracking_link_parameters_server_side`] = {
                messageKey: err('TRACKING_LINK_PARAMS_SERVER_SIDE_REQUIRED'),
                requiredKeys: [...REQUIRED_SERVER_SIDE_MACRO_KEYS],
            };
        }
    }
    if (generalPlatformsIncludeTrackingLinkExtraRulePlatform(generalPlatforms)) {
        const missingExtraKeys = TRACKING_LINK_EXTRA_REQUIRED_KEYS.filter(key => {
            const param = params.find(p => p.singular_key === key);
            return !param?.partner_macro?.trim();
        });
        if (missingExtraKeys.length) {
            const triggeringPlatforms = (generalPlatforms || []).filter(p =>
                TRACKING_LINK_EXTRA_RULE_PLATFORMS.includes(p)
            );
            errors[`formData.${ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS}.tracking_link_extra`] = {
                messageKey: err('TRACKING_LINK_PARAMS_EXTRA_RULE'),
                triggeringPlatforms,
                missingKeys: missingExtraKeys,
            };
        }
    }

    return errors;
};

const validatePostbackOverallSetup = (data, formData) => {
    const errors = {};
    const tabPath = `formData.${ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP}`;
    const entries = data.postback_events_and_platforms || [];
    const hasAnySelected = entries.some(e => (e.platforms || []).length > 0);
    if (!hasAnySelected) {
        errors[`${tabPath}.postback_events_and_platforms`] = err('POSTBACK_EVENTS_AND_PLATFORMS_REQUIRED');
    }
    const platformTemplate = formData?.[ATTRIBUTION_TAB_IDS.GENERAL]?.platform_template;
    const hasUrlPattern = !!PLATFORM_TEMPLATE_POSTBACK_URL_PATTERNS[platformTemplate];
    if (hasUrlPattern) {
        // Affiliate templates assemble postback_url_base from a fixed pattern + a user-supplied
        // domain. The base URL is non-empty even when the domain is blank, so the generic
        // `POSTBACK_URL_BASE_REQUIRED` check would never fire — emit a domain-specific error.
        const domain = extractPostbackUrlDomain(platformTemplate, data.postback_url_base);
        if (!domain.trim()) {
            errors[`${tabPath}.postback_url_domain`] = err('POSTBACK_URL_DOMAIN_REQUIRED');
        } else if (!POSTBACK_URL_DOMAIN_REGEX.test(domain)) {
            errors[`${tabPath}.postback_url_domain_invalid`] = err('POSTBACK_URL_DOMAIN_INVALID');
        }
        // Security token is optional, but if the user typed something it must be URL-safe and
        // we never persist an empty `?<key>=` (cleared input strips the key).
        if (PLATFORM_TEMPLATE_SECURITY_TOKEN_KEYS[platformTemplate]) {
            const token = extractSecurityToken(platformTemplate, data.postback_url_base);
            if (token && !POSTBACK_URL_SECURITY_TOKEN_REGEX.test(token)) {
                errors[`${tabPath}.postback_url_security_token_invalid`] = err('POSTBACK_URL_SECURITY_TOKEN_INVALID');
            }
        }
        // Required template query param (currently only Everflow's `nid`): must be filled, and
        // its value must pass the same URL-safe-character regex as the security token.
        if (PLATFORM_TEMPLATE_REQUIRED_QUERY_PARAM_KEYS[platformTemplate]) {
            const value = extractRequiredQueryParamValue(platformTemplate, data.postback_url_base);
            if (!value.trim()) {
                errors[`${tabPath}.postback_url_required_query_param`] = err('POSTBACK_URL_NID_REQUIRED');
            } else if (!POSTBACK_URL_SECURITY_TOKEN_REGEX.test(value)) {
                errors[`${tabPath}.postback_url_required_query_param_invalid`] = err('POSTBACK_URL_NID_INVALID');
            }
        }
    } else if (!data.postback_url_base?.trim()) {
        errors[`${tabPath}.postback_url_base`] = err('POSTBACK_URL_BASE_REQUIRED');
    }
    if (data.different_domains_per_postback) {
        // For affiliate templates, "filled" means the extracted domain matches the same regex the
        // BE enforces — a raw non-empty URL with an empty/illegal domain doesn't count.
        let hasValidEventUrl = false;
        let hasInvalidDomain = false;
        POSTBACK_EVENT_TYPE_OPTIONS.forEach(et => {
            const raw = data[`${et}_postback_url`];
            if (!raw?.trim()) return;
            if (!hasUrlPattern) {
                hasValidEventUrl = true;
                return;
            }
            // Match the BE: per-event URLs are validated against the event-type-specific pattern
            // when one exists (e.g. HasOffers conversions = /aff_lsr), not the template's base.
            const domain = extractPostbackUrlDomain(platformTemplate, raw, et);
            if (POSTBACK_URL_DOMAIN_REGEX.test(domain)) {
                hasValidEventUrl = true;
            } else if (domain.trim()) {
                hasInvalidDomain = true;
            }
        });
        if (!hasValidEventUrl) {
            errors[`${tabPath}.postback_urls`] = err('POSTBACK_URLS_REQUIRED_WHEN_DIFFERENT_DOMAINS');
        }
        if (hasInvalidDomain) {
            errors[`${tabPath}.postback_urls_invalid_domain`] = err('POSTBACK_URL_DOMAIN_INVALID_PER_EVENT');
        }
    }
    if (data.needs_app_level_macro) {
        const appMacros = data.app_level_macros || [];
        if (!appMacros.length) {
            errors[`${tabPath}.app_level_macros`] = err('APP_LEVEL_MACROS_REQUIRED');
        }
        appMacros.forEach((m, i) => {
            if (!m.parameter_key_name?.trim()) {
                errors[`${tabPath}.app_level_macros.${i}.parameter_key_name`] = err(
                    'APP_LEVEL_MACRO_PARAMETER_KEY_REQUIRED'
                );
            } else if (!URL_QUERY_KEY_REGEX.test(m.parameter_key_name)) {
                errors[`${tabPath}.app_level_macros.${i}.parameter_key_invalid_chars`] = err(
                    'APP_LEVEL_MACRO_PARAMETER_KEY_INVALID_CHARS_SUMMARY'
                );
            }
            if (!m.input_type) {
                errors[`${tabPath}.app_level_macros.${i}.input_type`] = err('APP_LEVEL_MACRO_INPUT_TYPE_REQUIRED');
            }
            if (m.input_type === MACRO_INPUT_TYPES.DROPDOWN && !m.allowed_values?.trim()) {
                errors[`${tabPath}.app_level_macros.${i}.allowed_values`] = err(
                    'APP_LEVEL_MACRO_ALLOWED_VALUES_REQUIRED'
                );
            }
        });
    }
    if (data.enable_event_mapping) {
        if (!data.event_parameter_key_name?.trim()) {
            errors[`${tabPath}.event_parameter_key_name`] = err('EVENT_PARAMETER_KEY_REQUIRED');
        } else if (!URL_QUERY_KEY_REGEX.test(data.event_parameter_key_name)) {
            errors[`${tabPath}.event_parameter_key_invalid_chars`] = err('EVENT_PARAMETER_KEY_INVALID_CHARS_SUMMARY');
        }
        const eventNames = data.event_names || [];
        if (!eventNames.length) {
            errors[`${tabPath}.event_names`] = err('EVENT_NAMES_REQUIRED');
        } else if (eventNames.some(n => !n?.trim())) {
            // ``addEventName`` seeds rows with '', so a forgotten empty row would otherwise pass
            // once at least one other row was filled in.
            errors[`${tabPath}.event_names_empty`] = err('EVENT_NAME_REQUIRED');
        }
        if (eventNames.some(n => n && WHITESPACE_REGEX.test(n))) {
            errors[`${tabPath}.event_names_no_spaces`] = err('EVENT_NAME_NO_SPACES_SUMMARY');
        }
        // Empty strings are duplicate-checked too: two empty rows are still two rows the user must fix.
        // Scoped to enable_event_mapping=true so server-seeded event_names with the section disabled
        // can't silently block Save Draft via the matching DRAFT_BLOCKING_ERROR_KEY_SUFFIX.
        const seenNames = new Set();
        const hasDuplicateEventName = eventNames.some(n => {
            const key = (n || '').trim();
            if (seenNames.has(key)) return true;
            seenNames.add(key);
            return false;
        });
        if (hasDuplicateEventName) {
            errors[`${tabPath}.event_names_duplicate`] = err('EVENT_NAME_DUPLICATE');
        }
    }
    return errors;
};

const validatePostbackTemplates = (data, formData, postbackMacroNetworkName) => {
    const errors = {};
    const tabPath = `formData.${ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES}`;
    const overallTab = formData?.[ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP] || {};
    const entries = overallTab.postback_events_and_platforms || [];
    const platformTemplate = formData?.[ATTRIBUTION_TAB_IDS.GENERAL]?.platform_template;
    entries.forEach(entry => {
        const eventType = entry.event_type;
        (entry.platforms || []).forEach(platform => {
            const rows = data?.[eventType]?.[platform] || [];
            // Required-by-template macros: catch BOTH the missing-row and empty-key cases here,
            // because the per-cell `partner_parameter_key_required` ("Fill the key or delete the
            // row") message excludes them — required rows can't be deleted, so that copy would be
            // misleading. Same split as IP on tracking-link parameters: the platform-required
            // summary owns it, the generic macro-missing summary skips it.
            const requiredMacrosForCell = new Set();
            const missingRequiredMacrosForCell = [];
            resolveTemplateEntries(platformTemplate, eventType, platform, postbackMacroNetworkName)
                .filter(e => e.required)
                .forEach(({ resolvedMacro }) => {
                    requiredMacrosForCell.add(resolvedMacro);
                    const row = rows.find(r => r.singular_macro === resolvedMacro);
                    if (!row?.partner_parameter_key?.trim()) {
                        missingRequiredMacrosForCell.push(resolvedMacro);
                    }
                });
            if (missingRequiredMacrosForCell.length) {
                // Per-cell so the user can see which (event_type, platform) the missing macros
                // belong to — aggregating across cells lost that information.
                errors[`${tabPath}.${eventType}.${platform}.required_by_template`] = {
                    messageKey: err('POSTBACK_TEMPLATE_REQUIRED_BY_TEMPLATE_SUMMARY'),
                    eventType,
                    platform,
                    macros: missingRequiredMacrosForCell,
                    template: platformTemplate,
                };
            }
            const filledRows = rows.filter(r => !!r.partner_parameter_key?.trim());
            // Skip when required-by-template macros exist — their summary already covers the empty case.
            if (!filledRows.length && requiredMacrosForCell.size === 0) {
                errors[`${tabPath}.${eventType}.${platform}.at_least_one_macro_required`] = {
                    messageKey: err('POSTBACK_TEMPLATE_AT_LEAST_ONE_MACRO'),
                    eventType,
                    platform,
                };
            }
            // Per-table uniqueness: two rows can't share the same partner_parameter_key — they
            // would collide as duplicate query keys in the rendered postback URL.
            const keyCounts = filledRows.reduce((acc, r) => {
                const k = r.partner_parameter_key.trim();
                acc[k] = (acc[k] || 0) + 1;
                return acc;
            }, {});
            const duplicateKey = Object.keys(keyCounts).find(k => keyCounts[k] > 1);
            if (duplicateKey) {
                errors[`${tabPath}.${eventType}.${platform}.partner_parameter_key_duplicate`] = {
                    messageKey: err('POSTBACK_TEMPLATE_PARAM_KEY_DUPLICATE'),
                    eventType,
                    platform,
                    macro: duplicateKey,
                };
            }
            const missingKeyMacros = [];
            const invalidCharsMacros = [];
            rows.forEach(row => {
                // Required-by-template rows are surfaced by the dedicated required-by-template
                // summary ("must be filled because you selected the X template"). Skip them here
                // so the user doesn't get told "Fill the key or delete the row" for a row that
                // can't actually be deleted — same dedupe pattern as IP missing on tracking-link
                // parameters: the platform-required summary owns the empty case, the generic
                // missing-macro summary excludes it.
                if (!row.partner_parameter_key?.trim() && !requiredMacrosForCell.has(row.singular_macro)) {
                    missingKeyMacros.push(row.singular_macro);
                }
                if (row.partner_parameter_key && !URL_QUERY_KEY_REGEX.test(row.partner_parameter_key)) {
                    invalidCharsMacros.push(row.singular_macro);
                }
            });
            if (invalidCharsMacros.length) {
                errors[`${tabPath}.${eventType}.${platform}.partner_parameter_key_invalid_chars`] = {
                    messageKey: err('POSTBACK_TEMPLATE_PARAM_KEY_INVALID_CHARS_SUMMARY'),
                    eventType,
                    platform,
                    macros: invalidCharsMacros,
                };
            }
            if (missingKeyMacros.length) {
                errors[`${tabPath}.${eventType}.${platform}.partner_parameter_key_required`] = {
                    messageKey: err('POSTBACK_TEMPLATE_PARAM_KEY_REQUIRED_SUMMARY'),
                    eventType,
                    platform,
                    macros: missingKeyMacros,
                };
            }
        });
    });
    return errors;
};

export const TAB_VALIDATORS = {
    [ATTRIBUTION_TAB_IDS.GENERAL]: data => validateRequired(ATTRIBUTION_TAB_IDS.GENERAL, data, GENERAL_RULES),
    [ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS]: validateTrackingLinkOptions,
    [ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP]: validatePostbackOverallSetup,
    [ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES]: validatePostbackTemplates,
};

const PLATFORM_LABEL_PREFIX = `${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.PLATFORM_OPTIONS`;
const PLATFORM_TEMPLATE_LABEL_PREFIX = `${TRANSLATE_PREFIX}.PLATFORM_TEMPLATES`;
const TRACKING_LINK_OPTIONS_TAB_PREFIX = `${TRANSLATE_PREFIX}.TRACKING_LINK_OPTIONS_TAB`;

// Turn a validator-produced info object into the `data` object passed to <Translate>.
// Validators run as pure functions without access to `translate`, so any label
// composition that needs translation happens here at render time.
export const buildErrorData = (info, translate) => {
    if (!info || typeof info !== 'object') return undefined;
    const data = {};
    const translateTrackingParamFieldName = k => {
        const key = `${TRACKING_LINK_OPTIONS_TAB_PREFIX}.PARAMS.${String(k).toUpperCase()}.FIELD_NAME`;
        const label = translate(key);
        return label === key ? String(k) : label;
    };
    if (info.eventType && info.platform) {
        const eventLabel = translate(getPostbackEventTypeLabelKey(info.eventType, info.platform));
        const platformLabel = translate(`${PLATFORM_LABEL_PREFIX}.${info.platform.toUpperCase()}`);
        data.combo = `${eventLabel} - ${platformLabel}`;
    }
    const code = s => `<code>${s}</code>`;
    if (info.macro) data.macro = code(info.macro);
    if (Array.isArray(info.macros) && info.macros.length) {
        data.macrosList = info.macros.map(code).join(', ');
    }
    if (Array.isArray(info.requiredKeys) && info.requiredKeys.length) {
        data.requiredKeysList = info.requiredKeys
            .map(translateTrackingParamFieldName)
            .map(code)
            .join(', ');
    }
    if (
        Array.isArray(info.triggeringPlatforms) &&
        info.triggeringPlatforms.length &&
        Array.isArray(info.missingKeys) &&
        info.missingKeys.length
    ) {
        data.platformsList = info.triggeringPlatforms
            .map(p => translate(`${PLATFORM_LABEL_PREFIX}.${String(p).toUpperCase()}`))
            .join(', ');
        data.keysList = info.missingKeys
            .map(translateTrackingParamFieldName)
            .map(code)
            .join(', ');
    }
    if (info.fieldName) data.fieldName = code(info.fieldName);
    if (Array.isArray(info.fieldNames) && info.fieldNames.length) {
        data.fieldNamesList = info.fieldNames.map(code).join(', ');
    }
    if (info.template) {
        const tmplKey = `${PLATFORM_TEMPLATE_LABEL_PREFIX}.${String(info.template).toUpperCase()}`;
        const label = translate(tmplKey);
        data.template = label === tmplKey ? String(info.template) : label;
    }
    return Object.keys(data).length ? data : undefined;
};
